import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { changelogAPI } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const ChangelogPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reactingId, setReactingId] = useState(null);
  const [animatingReactions, setAnimatingReactions] = useState({});
  const [formData, setFormData] = useState({
    category: 'Housekeeping',
    title: '',
    description: '',
    release_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  const categories = [
    'All',
    'Housekeeping',
    'IT',
    'Guest Relations',
    'Sales and Marketing',
    'Accounting and Finance',
    'Technical'
  ];

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await changelogAPI.list(selectedCategory);
      const payload = response?.data ?? response?.results;
      if (Array.isArray(payload)) {
        setEntries(payload);
      } else {
        setEntries([]);
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while loading updates.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const mergeEntry = (updated) => {
    if (!updated || !updated.id) return;
    setEntries((prev) =>
      prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e))
    );
  };

  const handleReaction = async (entryId, reactionType) => {
    const animKey = `${entryId}-${reactionType}`;
    setAnimatingReactions((prev) => ({ ...prev, [animKey]: true }));
    setTimeout(() => {
      setAnimatingReactions((prev) => {
        const next = { ...prev };
        delete next[animKey];
        return next;
      });
    }, 1000);

    try {
      setReactingId(entryId);
      const response = await changelogAPI.react(entryId, reactionType);
      const updated = response?.data;
      if (updated) mergeEntry(updated);
    } catch (err) {
      setError(err?.message || 'Reaksiyon kaydedilemedi.');
    } finally {
      setReactingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body = {
        category: formData.category,
        title: formData.title.trim(),
        description: formData.description.trim(),
        release_date: formData.release_date
      };
      if (editingEntry) {
        await changelogAPI.update(editingEntry.id, body);
      } else {
        await changelogAPI.create(body);
      }
      await fetchEntries();
      setShowModal(false);
      setEditingEntry(null);
      setFormData({
        category: 'Housekeeping',
        title: '',
        description: '',
        release_date: new Date().toISOString().split('T')[0]
      });
    } catch (err) {
      setError(err?.message || 'Kayıt kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      description: entry.description,
      release_date: entry.release_date
    });
    setShowModal(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    try {
      await changelogAPI.delete(entryId);
      await fetchEntries();
    } catch (err) {
      setError(err?.message || 'Silinemedi.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
      year: date.getFullYear(),
      fullDate: `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()} ${days[date.getDay()]}`
    };
  };

  const groupedEntries = entries.reduce((acc, entry) => {
    const dateKey = entry.release_date;
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(entry);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedEntries).sort((a, b) => new Date(b) - new Date(a));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white relative">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <button
          onClick={() => navigate('/module-selection')}
          className="mb-8 flex items-center text-gray-400 hover:text-white transition-colors group"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Module Selection
        </button>

        {error && (
          <div className="mb-6 rounded-lg border border-red-800/50 bg-red-950/40 px-4 py-3 text-sm text-red-200 flex justify-between gap-4">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="text-red-300 hover:text-white shrink-0">
              ×
            </button>
          </div>
        )}

        <div className="mb-16 text-center">
          <h1 className="text-6xl font-extrabold mb-4 text-white tracking-tight">Innovations</h1>
          <p className="text-gray-300 text-xl max-w-2xl mx-auto leading-relaxed">
            Latest updates, improvements, and fixes from the AKKA Hotels team.
          </p>
        </div>

        {isAdmin() && (
          <div className="absolute top-8 right-8">
            <button
              type="button"
              onClick={() => {
                setEditingEntry(null);
                setFormData({
                  category: 'Housekeeping',
                  title: '',
                  description: '',
                  release_date: new Date().toISOString().split('T')[0]
                });
                setShowModal(true);
              }}
              className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-full transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-110"
              title="Add New Update"
            >
              <PlusIcon className="h-6 w-6 text-white" />
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-12 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setSelectedCategory(category)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${selectedCategory === category
                ? 'bg-white text-gray-900 shadow-lg scale-105'
                : 'bg-slate-800/50 text-gray-300 hover:bg-slate-800 hover:text-white backdrop-blur-sm'
                }`}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No updates available yet.</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-32 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gray-500 via-gray-600 to-gray-500"></div>

            <div className="space-y-16">
              {sortedDates.map((dateKey) => {
                const dateInfo = formatDate(dateKey);
                const dateEntries = groupedEntries[dateKey];

                return (
                  <div key={dateKey} className="relative pl-40">
                    <div className="absolute left-32 top-0 z-10 -translate-x-1/2">
                      <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-slate-950"></div>
                    </div>

                    <div className="absolute left-0 top-0 w-32">
                      <div className="text-sm font-semibold text-white">
                        {dateInfo.date} {dateInfo.month} {dateInfo.year}
                      </div>
                      <div className="text-xs mt-1 text-sky-400">
                        {dateInfo.day}
                      </div>
                    </div>

                    <div className="space-y-6">
                      {dateEntries.map((entry) => (
                        <div
                          key={entry.id}
                          className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-300 backdrop-blur-sm"
                        >
                          <div className="mb-4">
                            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold bg-slate-900 text-purple-300 border border-purple-500/40">
                              {entry.category}
                            </span>
                          </div>

                          <h3 className="text-2xl font-bold mb-3 text-white leading-tight">{entry.title}</h3>

                          <p className="text-gray-300 mb-6 leading-relaxed text-base whitespace-pre-wrap">{entry.description}</p>

                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={reactingId === entry.id}
                                onClick={() => handleReaction(entry.id, 'thumbs_up')}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 hover:scale-105 transition-all duration-200 backdrop-blur-sm border border-slate-700/50 disabled:opacity-50"
                              >
                                <span
                                  className={`text-2xl inline-block ${animatingReactions[`${entry.id}-thumbs_up`]
                                    ? 'animate-bounce'
                                    : ''
                                    }`}
                                  style={{
                                    display: 'inline-block',
                                    transformOrigin: 'center',
                                    transform: animatingReactions[`${entry.id}-thumbs_up`]
                                      ? 'scale(2) rotate(15deg)'
                                      : 'scale(1) rotate(0deg)',
                                    transition: 'transform 0.3s ease-out'
                                  }}
                                >
                                  👍
                                </span>
                                <span className="text-sm font-semibold">{entry.thumbs_up_count || 0}</span>
                              </button>

                              <button
                                type="button"
                                disabled={reactingId === entry.id}
                                onClick={() => handleReaction(entry.id, 'rocket')}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 hover:scale-105 transition-all duration-200 backdrop-blur-sm border border-slate-700/50 disabled:opacity-50"
                              >
                                <span
                                  className={`text-2xl inline-block ${animatingReactions[`${entry.id}-rocket`]
                                    ? 'animate-bounce'
                                    : ''
                                    }`}
                                  style={{
                                    display: 'inline-block',
                                    transformOrigin: 'center',
                                    transform: animatingReactions[`${entry.id}-rocket`]
                                      ? 'scale(2) rotate(-15deg)'
                                      : 'scale(1) rotate(0deg)',
                                    transition: 'transform 0.3s ease-out'
                                  }}
                                >
                                  🚀
                                </span>
                                <span className="text-sm font-semibold">{entry.rocket_count || 0}</span>
                              </button>

                              <button
                                type="button"
                                disabled={reactingId === entry.id}
                                onClick={() => handleReaction(entry.id, 'confetti')}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 hover:scale-105 transition-all duration-200 backdrop-blur-sm border border-slate-700/50 disabled:opacity-50"
                              >
                                <span
                                  className={`text-2xl inline-block ${animatingReactions[`${entry.id}-confetti`]
                                    ? 'animate-bounce'
                                    : ''
                                    }`}
                                  style={{
                                    display: 'inline-block',
                                    transformOrigin: 'center',
                                    transform: animatingReactions[`${entry.id}-confetti`]
                                      ? 'scale(2) rotate(15deg)'
                                      : 'scale(1) rotate(0deg)',
                                    transition: 'transform 0.3s ease-out'
                                  }}
                                >
                                  🎉
                                </span>
                                <span className="text-sm font-semibold">{entry.confetti_count || 0}</span>
                              </button>
                            </div>

                            {isAdmin() && (
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(entry)}
                                  className="p-2.5 text-gray-400 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-all duration-200"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(entry.id)}
                                  className="p-2.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all duration-200"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 outline-none focus:outline-none overflow-y-auto">
          <div className="bg-slate-900 text-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-700/50 transform transition-all animate-in fade-in zoom-in duration-300">
            <h2 className="text-2xl font-bold mb-4">
              {editingEntry ? 'Edit Update' : 'Add New Update'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  required
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white min-h-[120px]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Release Date</label>
                <input
                  type="date"
                  value={formData.release_date}
                  onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEntry(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingEntry ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ChangelogPage;
